import { VenueDetailsBySlugResponse, useI18N } from "common/hooks";
import { createPageAnalytics } from "common/analytics";
import { GlobeIcon, MapPinIcon, ClockIcon } from "@phosphor-icons/react";
import OpeningHours from "@/components/VenueDetailBasic/OpeningHours";
import { Helmet } from "react-helmet-async";

type AboutTabProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse;
  onRefresh: () => void;
};

function AboutTab({ venueDetailsResponse }: AboutTabProps) {
  const venueDetails = venueDetailsResponse.details;
  const i18n = useI18N();

  // Format opening hours for display
  const formatTime = (time: number) => {
    if (typeof time !== "number" || isNaN(time)) {
      return i18n.get("invalidTime");
    }

    const hours = Math.floor(time / 100);
    const minutes = time % 100;
    const amPm = hours >= 12 ? i18n.get("pm") : i18n.get("am");
    const formattedHour = hours % 12 || 12;
    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes.toString();

    return `${formattedHour}:${formattedMinutes} ${amPm}`;
  };

  const getDayName = (day: number) => {
    const days = [
      i18n.get("sunday"),
      i18n.get("monday"),
      i18n.get("tuesday"),
      i18n.get("wednesday"),
      i18n.get("thursday"),
      i18n.get("friday"),
      i18n.get("saturday"),
    ];
    return days[day] || "";
  };

  const hasOpeningHours =
    venueDetails.openingHours &&
    venueDetails.openingHours.length > 0 &&
    venueDetails.openingHours[0]?.mode === 0;

  return (
    <>
      <Helmet>
        <title>
          {venueDetails?.name
            ? `${venueDetails.name.length > 30 ? venueDetails.name.substring(0, 30) + "..." : venueDetails.name} | ${i18n.get("aboutPageTitleSuffix")}`
            : i18n.get("aboutPageTitleSuffix")}
        </title>
      </Helmet>

      <div className="flex flex-col w-full overflow-x-hidden">
        {/* Venue Information */}
        <section
          className="flex flex-col gap-3 pb-12 w-full min-w-0"
          aria-labelledby="venue-info-heading"
        >
          <h2 id="venue-info-heading" className="sr-only">
            {i18n.get("venueInformation")}
          </h2>

            {/* Opening Hours Section */}
            {hasOpeningHours && (
              <>
                <OpeningHours
                  openingHours={venueDetails.openingHours}
                  containerClassName="cursor-pointer"
                />

                {/* Divider */}
                <hr
                  className="border-t border-border-token-brand-minimal w-full"
                  aria-hidden="true"
                />
              </>
            )}

          

            {/* Address Section */}
            {venueDetails?.address && (
              <>
                <article className="flex flex-col w-full">
                  <h3 className="label-s-medium text-fg-neutral-subtle min-h-[34px] flex items-center">
                    {i18n.get("address")}
                  </h3>
                  <div className="flex items-center min-h-[34px]">
                    <address className="label-m-medium text-fg-neutral-strong not-italic">
                      {venueDetails.address}
                    </address>
                  </div>
                </article>
                {/* Divider */}
                <hr
                  className="border-t border-border-token-brand-minimal w-full"
                  aria-hidden="true"
                />
              </>
            )}

           

            {/* Website Section */}
            {venueDetails?.website && (
              <>
                <article className="flex flex-col w-full overflow-hidden">
                  <h3 className="label-s-medium text-fg-neutral-subtle min-h-[34px] flex items-center">
                    {i18n.get("website")}
                  </h3>
                  <div className="flex items-center min-h-[34px]">
                    <a
                      href={venueDetails.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        // website_link_button -> place_external_link_cta_activated.
                        createPageAnalytics("place").track("website_link_button", {
                          booking_platform: "",
                          brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
                          brand_name: venueDetails?.brandName ?? "",
                          business_id:
                            venueDetails?.business?.id != null
                              ? String(venueDetails.business.id)
                              : "",
                          link_destination: "website",
                          place_name: venueDetails?.name ?? "",
                        });
                      }}
                      className="label-m-medium text-fg-neutral-strong hover:underline focus-blue rounded break-all"
                    >
                      {venueDetails.website}
                    </a>
                  </div>
                </article>

                {/* Divider */}
                <hr
                  className="border-t border-border-token-brand-minimal w-full"
                  aria-hidden="true"
                />
              </>
            )}

          

            {/* Phone Number Section */}
            {venueDetails?.phone && (
              <article className="flex flex-col w-full">
                <h3 className="label-s-medium text-fg-neutral-subtle min-h-[34px] flex items-center">
                  {i18n.get("phoneNumber")}
                </h3>
                <div className="flex items-center min-h-[34px]">
                  <span className="label-m-medium text-fg-neutral-strong not-italic">
                    {venueDetails.phone}
                  </span>
                </div>
              </article>
            )}
        </section>
      </div>
    </>
  );
}

export default AboutTab;
